import { useMemo, useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Layout } from "@/components/layout";
import { useLanguage } from "@/contexts/language";
import { fetchWithAuth } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { ExternalLink, ScrollText } from "lucide-react";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";

type CadorsTab = "cases" | "about";
type CadorsScope = "all" | "accidents";

type DailyPayload = {
  reportingDate: string;
  reportingDateTo: string | null;
  lang: string;
  scope: CadorsScope;
  fetchedAt: string;
  source: string;
  officialReportUrl: string;
  occurrenceCount: number;
  totalMatching: number;
  truncated: boolean;
  occurrences: Array<{
    cadorsNumber: string;
    occurrenceType: string;
    narrative: string;
    categories: string[];
    sourceReportingDate?: string;
  }>;
};

function utcTodayYmd(): string {
  return new Date().toISOString().slice(0, 10);
}

function utcMonthAgoYmd(): string {
  const d = new Date();
  d.setUTCMonth(d.getUTCMonth() - 1);
  return d.toISOString().slice(0, 10);
}

function utcYesterdayYmd(): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}

async function fetchCadorsDaily(params: {
  reportingDate: string;
  reportingDateTo?: string | null;
  lang: "en" | "fr";
  scope: CadorsScope;
}): Promise<DailyPayload> {
  const q = new URLSearchParams({
    reportingDate: params.reportingDate,
    lang: params.lang,
    scope: params.scope,
  });
  if (params.reportingDateTo) q.set("reportingDateTo", params.reportingDateTo);
  return fetchWithAuth<DailyPayload>(`/api/cadors/daily?${q.toString()}`);
}

export default function CadorsPage() {
  const { t, language } = useLanguage();
  const c = t.cadors;
  const { toast } = useToast();
  const [tab, setTab] = useState<CadorsTab>("cases");
  const dateMin = useMemo(() => utcMonthAgoYmd(), []);
  const dateMax = useMemo(() => utcTodayYmd(), []);

  const [scope, setScope] = useState<CadorsScope>("all");
  const [dateInput, setDateInput] = useState(utcYesterdayYmd);
  const [activeDate, setActiveDate] = useState(utcYesterdayYmd);

  const [dateFromInput, setDateFromInput] = useState(utcYesterdayYmd);
  const [dateToInput, setDateToInput] = useState(utcYesterdayYmd);
  const [activeFrom, setActiveFrom] = useState(utcYesterdayYmd);
  const [activeTo, setActiveTo] = useState(utcYesterdayYmd);

  useEffect(() => {
    const clamp = (v: string) => (v < dateMin ? dateMin : v > dateMax ? dateMax : v);
    setDateInput((d) => clamp(d));
    setDateFromInput((d) => clamp(d));
    setDateToInput((d) => clamp(d));
    setActiveDate((d) => clamp(d));
    setActiveFrom((d) => clamp(d));
    setActiveTo((d) => clamp(d));
  }, [dateMin, dateMax]);

  const queryArgs = useMemo(() => {
    if (scope === "accidents") {
      const useRange = activeTo > activeFrom;
      return {
        reportingDate: activeFrom,
        reportingDateTo: useRange ? activeTo : null,
        lang: language as "en" | "fr",
        scope,
      };
    }
    return {
      reportingDate: activeDate,
      reportingDateTo: null as string | null,
      lang: language as "en" | "fr",
      scope,
    };
  }, [scope, activeDate, activeFrom, activeTo, language]);

  const query = useQuery({
    queryKey: ["cadors-daily", queryArgs.reportingDate, queryArgs.reportingDateTo, queryArgs.lang, queryArgs.scope],
    queryFn: () => fetchCadorsDaily(queryArgs),
    staleTime: 60_000,
  });

  const errMsg = useMemo(() => {
    const e = query.error as Error & { status?: number };
    if (!query.isError) return "";
    const raw = String(e?.message ?? "error");
    if (raw.includes("404") || raw.toLowerCase().includes("not enabled")) return c.errorDisabled;
    return c.errorGeneric;
  }, [query.isError, query.error, c.errorDisabled, c.errorGeneric]);

  const handleLoad = () => {
    if (scope === "accidents") {
      const from = dateFromInput < dateMin ? dateMin : dateFromInput > dateMax ? dateMax : dateFromInput;
      let to = dateToInput < dateMin ? dateMin : dateToInput > dateMax ? dateMax : dateToInput;
      if (to < from) {
        toast({ title: c.rangeInvalidTitle, description: c.rangeInvalidDesc, variant: "destructive" });
        return;
      }
      setActiveFrom(from);
      setActiveTo(to);
      return;
    }
    const d = dateInput < dateMin ? dateMin : dateInput > dateMax ? dateMax : dateInput;
    setActiveDate(d);
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <ScrollText className="h-7 w-7 text-primary shrink-0" aria-hidden />
            {c.pageTitle}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">{c.pageSubtitle}</p>
          <p className="text-[11px] font-mono text-muted-foreground/80 mt-1">{c.dateWindowHint}</p>
        </div>

        <div className="flex gap-2 border-b border-border">
          {(["cases", "about"] as const).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setTab(k)}
              className={cn(
                "px-4 py-2 text-sm font-mono uppercase tracking-wide border-b-2 -mb-px transition-colors",
                tab === k ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              {k === "cases" ? c.tabCases : c.tabAbout}
            </button>
          ))}
        </div>

        {tab === "about" && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base font-mono">{c.tabAbout}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-muted-foreground">
              <p>{c.disclaimer}</p>
              <a
                href="https://wwwapps.tc.gc.ca/Saf-Sec-Sur/2/cadors-screaq/m.aspx?lang=eng"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-primary hover:underline font-mono text-xs"
              >
                <ExternalLink className="h-4 w-4" />
                Transport Canada — CADORS
              </a>
            </CardContent>
          </Card>
        )}

        {tab === "cases" && (
          <div className="space-y-4">
            <div className="space-y-3">
              {scope === "all" ? (
                <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
                  <div className="flex-1 space-y-1">
                    <label htmlFor="cadors-date" className="text-xs font-mono uppercase text-muted-foreground">
                      {c.reportingDateLabel}
                    </label>
                    <Input
                      id="cadors-date"
                      type="date"
                      value={dateInput}
                      min={dateMin}
                      max={dateMax}
                      onChange={(e) => setDateInput(e.target.value)}
                      className="font-mono max-w-xs"
                    />
                  </div>
                  <Button type="button" className="font-mono uppercase shrink-0" onClick={handleLoad}>
                    {c.load}
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground font-mono">{c.accidentsDateRangeIntro}</p>
                  <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:items-end">
                    <div className="space-y-1">
                      <label htmlFor="cadors-from" className="text-xs font-mono uppercase text-muted-foreground">
                        {c.dateFromLabel}
                      </label>
                      <Input
                        id="cadors-from"
                        type="date"
                        value={dateFromInput}
                        min={dateMin}
                        max={dateMax}
                        onChange={(e) => setDateFromInput(e.target.value)}
                        className="font-mono max-w-[11rem]"
                      />
                    </div>
                    <div className="space-y-1">
                      <label htmlFor="cadors-to" className="text-xs font-mono uppercase text-muted-foreground">
                        {c.dateToLabel}
                      </label>
                      <Input
                        id="cadors-to"
                        type="date"
                        value={dateToInput}
                        min={dateMin}
                        max={dateMax}
                        onChange={(e) => setDateToInput(e.target.value)}
                        className="font-mono max-w-[11rem]"
                      />
                    </div>
                    <Button type="button" className="font-mono uppercase shrink-0" onClick={handleLoad}>
                      {c.load}
                    </Button>
                  </div>
                </div>
              )}

              <div className="space-y-1">
                <span className="text-xs font-mono uppercase text-muted-foreground block">{c.filterLabel}</span>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant={scope === "all" ? "default" : "outline"}
                    size="sm"
                    className="font-mono text-xs"
                    onClick={() => setScope("all")}
                  >
                    {c.filterAll}
                  </Button>
                  <Button
                    type="button"
                    variant={scope === "accidents" ? "default" : "outline"}
                    size="sm"
                    className="font-mono text-xs"
                    onClick={() => setScope("accidents")}
                  >
                    {c.filterAccidents}
                  </Button>
                </div>
                {scope === "accidents" && (
                  <p className="text-[11px] text-muted-foreground leading-snug max-w-xl">{c.filterAccidentsHelp}</p>
                )}
              </div>
            </div>

            {query.isLoading && (
              <div className="space-y-3">
                <Skeleton className="h-4 w-48" />
                <Skeleton className="h-24 w-full" />
                <Skeleton className="h-24 w-full" />
              </div>
            )}

            {query.isError && (
              <Card className="border-destructive/40">
                <CardContent className="pt-6 text-sm text-destructive font-mono">{errMsg}</CardContent>
              </Card>
            )}

            {query.isSuccess && query.data && (
              <>
                <div className="flex flex-wrap items-center justify-between gap-2 text-xs font-mono text-muted-foreground">
                  <div>
                    {query.data.reportingDateTo && query.data.reportingDateTo !== query.data.reportingDate ? (
                      <span className="block text-foreground/90">
                        {c.rangeSummary
                          .replace("{from}", query.data.reportingDate)
                          .replace("{to}", query.data.reportingDateTo)}
                      </span>
                    ) : null}
                    <span>{c.occurrenceCount.replace("{n}", String(query.data.occurrenceCount))}</span>
                    {(query.data.truncated || query.data.totalMatching !== query.data.occurrenceCount) && (
                      <span className="block mt-0.5 opacity-80">
                        {c.matchLine
                          .replace("{shown}", String(query.data.occurrenceCount))
                          .replace("{total}", String(query.data.totalMatching))}
                      </span>
                    )}
                  </div>
                  <a
                    href={query.data.officialReportUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-primary hover:underline"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    {c.openOfficial}
                  </a>
                </div>
                {query.data.truncated && (
                  <p className="text-xs text-amber-500/90 font-mono">
                    {c.truncated
                      .replace("{shown}", String(query.data.occurrenceCount))
                      .replace("{total}", String(query.data.totalMatching))
                      .replace("{max}", "80")}
                  </p>
                )}
                {query.data.occurrences.length === 0 ? (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">
                        {query.data.scope === "accidents" ? c.emptyAccidentsTitle : c.emptyTitle}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm text-muted-foreground">
                      {query.data.scope === "accidents" ? c.emptyAccidentsBody : c.emptyBody}
                    </CardContent>
                  </Card>
                ) : (
                  <ul className="space-y-3">
                    {query.data.occurrences.map((o) => (
                      <li key={`${o.cadorsNumber}-${o.sourceReportingDate ?? ""}`}>
                        <Card>
                          <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-mono text-primary">{o.cadorsNumber}</CardTitle>
                            {query.data.scope === "all" && o.occurrenceType && (
                              <p className="text-[11px] font-mono text-muted-foreground">
                                {c.typeLabel}: <span className="text-foreground/80">{o.occurrenceType}</span>
                              </p>
                            )}
                            {query.data.scope === "accidents" && o.sourceReportingDate && (
                              <p className="text-[11px] font-mono text-muted-foreground">
                                {c.reportingDateLabelShort}:{" "}
                                <span className="text-foreground/80">{o.sourceReportingDate}</span>
                              </p>
                            )}
                            {o.categories.length > 0 && (
                              <p className="text-[11px] font-mono text-muted-foreground">
                                {c.categoriesLabel}: {o.categories.join(" · ")}
                              </p>
                            )}
                          </CardHeader>
                          <CardContent className="text-sm leading-relaxed text-foreground/90">{o.narrative}</CardContent>
                        </Card>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
